import {memo} from 'react'
import {graphql, useFragment} from 'react-relay/hooks'
import {Link} from 'react-router-dom'
import {Box, Flex, Stack} from '@chakra-ui/react'
import {faUser as fasUser} from '@fortawesome/pro-solid-svg-icons'

import {UserCard_user$key} from './__generated__/UserCard_user.graphql'

import {FontAwesomeIcon} from 'components/FontAwesomeIcon'
import {Heading} from 'components/Heading'
import {Image} from 'components/Image'

import {UserFollowButton} from '../UserFollowButton'

interface UserCardProps {
  user: UserCard_user$key
}

export const UserCard = memo(function UserCard(props: UserCardProps) {
  const user = useFragment(
    graphql`
      fragment UserCard_user on User {
        ...UserFollowButton_user
        displayName
        resourcePath
        followerPrivacy
        viewerCanFollow
        avatar {
          transformedSrc(maxHeight: 160, maxWidth: 160)
        }
        city {
          name
          province {
            shortName
          }
        }
        followers(first: 1) {
          pageInfo {
            total
          }
        }
        following(first: 1) {
          pageInfo {
            total
          }
        }
      }
    `,
    props.user
  )

  return (
    <Flex
      bg="white"
      borderRadius="md"
      boxShadow="sm"
      direction={{base: `column`, lg: `row`}}
      justifyContent="center"
      flex={1}
      overflow="hidden"
      p={3}
    >
      <Flex
        align="center"
        as={Link}
        bg="gray.100"
        borderRadius="md"
        alignSelf="center"
        flexShrink={0}
        h={{base: 150, sm: 160}}
        justify="center"
        to={user.resourcePath}
        w={{base: 150, sm: 160}}
      >
        {user.avatar ? (
          <Image
            borderRadius="inherit"
            flex={1}
            src={user.avatar.transformedSrc}
          />
        ) : (
          <FontAwesomeIcon icon={fasUser} size="3x" />
        )}
      </Flex>
      <Flex
        direction="column"
        flex={1}
        justify="space-between"
        ml={{base: 0, md: 4}}
        mt={{base: 8, md: 0}}
        overflow="hidden"
      >
        <Box>
          <Heading
            isTruncated
            as="h3"
            color="gray.700"
            fontSize="2xl"
            fontWeight="semibold"
          >
            <Link to={user.resourcePath}>{user.displayName}</Link>
          </Heading>
          {user.city && (
            <Box color="gray.500" fontSize="sm" fontWeight="semibold">
              {user.city.name}, {user.city.province.shortName}
            </Box>
          )}
        </Box>
        <Stack
          bg="gray.100"
          borderRadius="md"
          direction="row"
          lineHeight="none"
          p={2}
          spacing={12}
        >
          <Stack>
            <Box color="gray.500" fontSize="sm" fontWeight="medium">
              Following
            </Box>
            <Box fontSize="3xl" fontWeight="medium">
              {user.following.pageInfo.total}
            </Box>
          </Stack>
          <Stack>
            <Box color="gray.500" fontSize="sm" fontWeight="medium">
              Followers
            </Box>
            <Box fontSize="3xl" fontWeight="medium">
              {user.followers.pageInfo.total}
            </Box>
          </Stack>
        </Stack>
        {user.viewerCanFollow && !user.followerPrivacy && <UserFollowButton size="sm" user={user} />}
      </Flex>
    </Flex>
  )
})
